import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { OutlineLink } from "../components/OutlineLink";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maintained by the client. Update this line when the diary year changes.
const DIARY_LINE =
  "The diary for the current commission is closed; the next Life Study begins in 2027.";

const Commission = () => {
  const [form, setForm] = useState({ name: "", email: "", telephone: "", message: "", company: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please share your name, email, and a few words about the life in question.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        message: form.message.trim(),
        company: form.company,
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError("Something interrupted the sending. Please try again, or write to us directly at info@theravensquill.co.za.");
    }
  };

  const inputClass =
    "w-full border border-[#A8A296] bg-transparent px-4 py-3 min-h-[48px] text-[16px] outline-none transition-colors focus:border-[#7A2E2E] text-[#1C1A17] placeholder:text-[#6E6A5E] placeholder:italic rounded-none";

  return (
    <div data-testid="commission-page">
      <Seo
        title="Commission a Private Biography · The Raven's Quill"
        description="A commission begins not with a fee but with a conversation. Request a private consultation, in confidence."
        path="/commission"
      />
      <section className="pt-20 pb-16 md:pt-28 md:pb-20">
        <Column>
          <Signature label="The Commission" testid="commission-signature" />
          <FadeIn>
            <h1 className="text-[32px] md:text-[38px] italic leading-[1.25] text-[#1C1A17]" data-testid="commission-headline">
              It Begins with a Conversation
            </h1>
          </FadeIn>
          <div className="mt-10 space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              The Raven&rsquo;s Quill undertakes only a few Life Studies at a time, so that each
              life receives the whole of the attention this work demands. A commission
              begins not with a fee, but with a conversation, to understand the life in
              question and whether this is the right way to preserve it.
            </FadeIn>
            <FadeIn as="p">
              Commissions are undertaken wherever the life is lived. The residency
              travels.
            </FadeIn>
            <FadeIn as="p" data-testid="commission-diary-line">
              {DIARY_LINE}
            </FadeIn>
            <FadeIn as="p">
              This page concerns The Life Study. A Final Tribute, needed in the days
              before a farewell, begins instead with a telephone call or WhatsApp message:{" "}
              <a
                href="https://wa.me/27792818435"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1C1A17] underline underline-offset-4 hover:text-[#7A2E2E] transition-colors"
                data-testid="commission-tribute-phone"
              >
                +27 79 281 8435
              </a>
            </FadeIn>
          </div>
        </Column>
      </section>

      <section className="pb-14">
        <Column>
          <Signature label="What to Expect" testid="what-to-expect-signature" />
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              A first private consultation establishes the shape of the life and the fit
              of the work. Should both sides wish to continue, a full prospectus
              follows, detailing the methodology, the timeline, the deliverables, and
              the investment. Only then is a commission confirmed.
            </FadeIn>
            <FadeIn as="p">Discretion is assumed at every stage.</FadeIn>
            <FadeIn as="p">
              Many enquiries come not from the subject but from a son or daughter, or
              from a family&rsquo;s adviser, wishing to honour a parent. These are
              welcomed, and handled with the same discretion.
            </FadeIn>
          </div>
          <FadeIn>
            <p className="mt-8 text-[15px] leading-[1.7] text-[#6E6A5E]" data-testid="commission-faq-link">
              For the questions most often asked before this conversation:{" "}
              <Link
                to="/faq"
                className="italic text-[#6E6A5E] underline underline-offset-4 hover:text-[#7A2E2E] transition-colors"
              >
                Questions, Answered Plainly
              </Link>
            </p>
          </FadeIn>
        </Column>
      </section>

      <section className="pb-14" data-testid="commission-principles">
        <Column>
          <Signature label="The Principles" testid="principles-signature" />
          <FadeIn>
            <h2 className="text-[24px] md:text-[26px] italic leading-[1.3] text-[#1C1A17]">
              The Honesty of the Arrangement
            </h2>
          </FadeIn>
          <FadeIn as="p" className="mt-8 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            Clarity is established before a word is written, because the trust this work
            requires depends on it. Three principles hold throughout a Life Study.
          </FadeIn>
          <div className="mt-12 space-y-12">
            <FadeIn>
              <h3 className="text-[20px] md:text-[22px] italic text-[#1C1A17]">
                Authorship, Held; the Work, Yours to Keep
              </h3>
              <p className="mt-5 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                The Raven&rsquo;s Quill authors every volume and retains the authorship
                and copyright of the work, as a scholar does of a study. This is what
                frees the subject from the vanity of self-praise, and what makes each
                volume an account that can be believed. What the family holds is
                everything else: the heritage run, a perpetual licence to reprint the
                volume privately, at cost, for as long as the lineage endures, and an
                absolute veto over anything private. The press undertakes in writing that
                the work is never published or shown without the family&rsquo;s consent.
                Nothing is ever taken from you. What is kept is only the author&rsquo;s
                name on the author&rsquo;s work.
              </p>
            </FadeIn>
            <FadeIn>
              <h3 className="text-[20px] md:text-[22px] italic text-[#1C1A17]">
                Narrative Sovereignty, Fully Retained
              </h3>
              <p className="mt-5 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                Independence of authorship never means loss of control over what is
                private. The subject holds a strict veto over intimate or confidential
                material, and nothing enters the public sphere over their objection.
                Dedicated months of the engagement are set aside, and structurally
                protected, for the subject to read and challenge the work while it is
                still soft.
              </p>
            </FadeIn>
            <FadeIn>
              <h3 className="text-[20px] md:text-[22px] italic text-[#1C1A17]">
                Discretion, Without Exception
              </h3>
              <p className="mt-5 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                Original papers, letters, and photographs remain the property of the
                subject and are returned on completion. What is shared in confidence
                stays in confidence.
              </p>
            </FadeIn>
          </div>
        </Column>
      </section>

      <section className="pb-24 md:pb-32">
        <Column>
          {status === "done" ? (
            <FadeIn>
              <div className="py-10" data-testid="enquiry-confirmation">
                <hr className="border-0" style={{ width: "72px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
                <p className="mt-8 text-[20px] md:text-[22px] italic leading-[1.6] text-[#1C1A17]">
                  Thank you. Your enquiry has reached us, in confidence. We reply
                  personally, usually within two working days.
                </p>
              </div>
            </FadeIn>
          ) : (
            <FadeIn>
              <p className="text-[18px] md:text-[20px] italic leading-[1.6] text-[#1C1A17] mb-12">
                To begin, please share a little about the life you have in mind, in
                confidence.
              </p>

              <form onSubmit={onSubmit} className="space-y-10" data-testid="enquiry-form" noValidate>
                {/* Honeypot: hidden from people, catches automated submissions */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company}
                    onChange={update("company")}
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-3" htmlFor="name">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className={inputClass}
                    data-testid="enquiry-name"
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-3" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className={inputClass}
                    data-testid="enquiry-email"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-3" htmlFor="telephone">
                    Telephone, optional
                  </label>
                  <input
                    id="telephone"
                    type="tel"
                    value={form.telephone}
                    onChange={update("telephone")}
                    className={inputClass}
                    data-testid="enquiry-telephone"
                    autoComplete="tel"
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-3" htmlFor="message">
                    Whom would the commission honour, and what makes their life one to preserve?
                  </label>
                  <textarea
                    id="message"
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    className={`${inputClass} resize-none leading-[1.8]`}
                    data-testid="enquiry-message"
                  />
                </div>

                {error && (
                  <p className="text-[14px] italic text-[#7A2E2E]" data-testid="enquiry-error">
                    {error}
                  </p>
                )}

                <div className="pt-2">
                  <OutlineLink
                    as="button"
                    type="submit"
                    testid="enquiry-submit"
                    onClick={undefined}
                  >
                    {status === "submitting" ? "Sending" : "Request a Private Consultation"}
                  </OutlineLink>
                </div>
              </form>
            </FadeIn>
          )}
        </Column>
      </section>
    </div>
  );
};

export default Commission;
